import { useRef } from "react";
import { useTheme } from "next-themes";
import { cn } from "@/lib/utils";
import { assetUrl } from "@/lib/assetUrl";
import type { AnvilAnimationVariant } from "./workingStatus";

// The seeded `active` pool: the hand-drawn set plus programmatic variants
// generated by scripts/generate-anvil-variants.mjs (eureka, on-fire, orbiting,
// sleuthing, swimming, zen). `caffeinating` is generated too but deliberately
// absent here: it is bound to waiting on sub-agents, so drawing it at random
// would drain the signal.
export const ANIMATION_VARIANTS = ["beautiful-mind", "concentrate", "considering", "doomscrolling", "eureka", "on-fire", "orbiting", "pong", "pondering", "ruminating", "running", "sleuthing", "swimming", "thinking", "zen"] as const;

/** The one PNG each non-seeded variant maps to. */
const VARIANT_ASSETS: Record<Exclude<AnvilAnimationVariant, "active">, string> = {
  type: "type",
  idle: "idle",
  ruminating: "ruminating",
  caffeinating: "caffeinating-hold",
  sipping: "caffeinating-sip"
};
const STATUS_WORDS = ["Thinking", "Pondering", "Forging", "Hammering", "Shaping", "Drafting", "Analyzing", "Synthesizing", "Constructing", "Assembling", "Conjuring", "Refining", "Brewing", "Sculpting", "Weaving", "Polishing", "Tinkering", "Calibrating", "Designing", "Iterating", "Compiling", "Computing", "Deciphering", "Evaluating", "Exploring", "Formulating", "Investigating", "Mulling", "Processing", "Reasoning", "Strategizing", "Contemplating", "Deliberating", "Devising", "Envisioning", "Imagining", "Mapping", "Orchestrating", "Plotting", "Preparing", "Puzzling", "Ruminating", "Scheming", "Smelting", "Sparking", "Tempering", "Welding", "Wiring", "Crunching", "Hatching"] as const;
function stableIndex(seed: string, modulo: number): number {
  let hash = 2166136261;
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i);
    hash = Math.imul(hash, 16777619);
  }
  return Math.abs(hash) % modulo;
}
export type AnvilActiveVariant = (typeof ANIMATION_VARIANTS)[number];

/**
 * Seed-stable pick from the `active` PNG set. Every surface that renders the
 * anvil character for the same turn (transcript indicator, scroll-to-bottom
 * pill) resolves through this so they all show the same animation.
 */
export function seededAnvilActiveVariant(seed: string): AnvilActiveVariant {
  return ANIMATION_VARIANTS[stableIndex(`${seed}:variant`, ANIMATION_VARIANTS.length)]!;
}

/**
 * Asset path for an anvil animation. `activeVariant` is only consulted when
 * `animationVariant` is `active`; the other variants map to a single PNG each.
 */
export function anvilAnimationSrc(animationVariant: AnvilAnimationVariant, activeVariant: AnvilActiveVariant, themeSuffix: "light" | "dark"): string {
  const variantName = animationVariant === "active" ? activeVariant : VARIANT_ASSETS[animationVariant];
  return assetUrl(`anvil-animations/anvil-${variantName}-${themeSuffix}.png`);
}

/** Every PNG the working indicator can reach, for prefetch. */
export const ANVIL_ANIMATION_ASSETS = Array.from(new Set([...ANIMATION_VARIANTS, ...Object.values(VARIANT_ASSETS)]));
export function AnvilWorkingStatus({
  seed,
  animationVariant = "active",
  showLabel = true,
  animateIn = true,
  animateOut = false,
  statusLabel
}: {
  seed?: string;
  /** Which anvil PNG set to use; see {@link AnvilAnimationVariant}. */
  animationVariant?: AnvilAnimationVariant;
  /** When false, only the anvil animation is shown (no status line). */
  showLabel?: boolean;
  /** Prefetched forge-shop loading line; stays put for the turn. */
  statusLabel?: string;
  /**
   * Whether to play the entrance animation when this element is inserted.
   * Pass false when the anvil is mounting as part of a larger remount
   * (e.g. switching sessions) rather than genuinely appearing.
   */
  animateIn?: boolean;
  /**
   * When true, plays the exit animation instead. The animation's fill mode
   * keeps the element invisible after it finishes; the parent is
   * responsible for unmounting once the animation has run (see
   * ANVIL_EXIT_MS in useAnvilPresence).
   */
  animateOut?: boolean;
}) {
  const {
    resolvedTheme
  } = useTheme();
  const variantRef = useRef(ANIMATION_VARIANTS[Math.floor(Math.random() * ANIMATION_VARIANTS.length)]!);
  const wordRef = useRef(STATUS_WORDS[Math.floor(Math.random() * STATUS_WORDS.length)]);
  const hasSeed = seed !== undefined;
  const activeVariant = hasSeed ? seededAnvilActiveVariant(seed) : variantRef.current;
  const seededWord = hasSeed ? STATUS_WORDS[stableIndex(`${seed}:word`, STATUS_WORDS.length)] : wordRef.current;
  const word = statusLabel?.trim() || seededWord || "Thinking";
  const suffix = resolvedTheme === "dark" ? "dark" : "light";
  const src = anvilAnimationSrc(animationVariant, activeVariant, suffix);
  return <div className={cn("flex items-center gap-1.5 py-1", animateOut ? "animate-anvil-exit" : animateIn && "animate-anvil-enter")}>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img src={src} alt="" width={40} height={40} draggable={false} className="select-none" />
      {showLabel ? <span className="type-body-s-regular text-neutral-900 dark:text-neutral-100">
          {word}
        </span> : null}
    </div>;
}